/**
 * Layout Constants for Note AI Edit Feature
 *
 * Centralized layout dimensions and spacing values used across
 * the note editing interface.
 */

/**
 * Fixed dimensions for major layout sections
 */
export const LAYOUT_DIMENSIONS = {
  /** Width of the chat sidebar in pixels */
  CHAT_SIDEBAR_WIDTH: 450,

  /** Maximum width for note section content in pixels */
  NOTE_SECTION_MAX_WIDTH: 800,

  /** Width of the table of contents sidebar in pixels */
  TOC_WIDTH: 60,

  /** Maximum width for inline TOC on ultrawide screens (xl breakpoint) in pixels */
  TOC_INLINE_MAX_WIDTH: 250,

  /** Minimum width for inline TOC on ultrawide screens (xl breakpoint) in pixels */
  TOC_INLINE_MIN_WIDTH: 200,

  /** Height of the sticky top bar in pixels */
  TOP_BAR_HEIGHT: 64,

  /** Height of the note metadata section in pixels */
  METADATA_HEIGHT: 80,

  /** Top offset for sticky TOC (accounts for top bar height + spacing) */
  TOC_STICKY_TOP: 132,

  /** Scroll offset for sticky headers (TopBar + metadata + spacing) */
  SCROLL_OFFSET: 170,
} as const;

/**
 * Responsive spacing values for layout elements
 */
export const LAYOUT_SPACING = {
  /** Responsive horizontal padding for note sections */
  SECTION_HORIZONTAL_PADDING: {
    xs: '16px',
    sm: '24px',
    md: '32px',
  },
  SECTION_HORIZONTAL_MARGIN: 'auto',
} as const;

/**
 * Computed maxWidth values that include padding
 * Ensures content width stays at NOTE_SECTION_MAX_WIDTH (800px)
 * while container expands to accommodate padding
 */
export const SECTION_MAX_WIDTH_WITH_PADDING = {
  xs: LAYOUT_DIMENSIONS.NOTE_SECTION_MAX_WIDTH + 16 * 2, // 832px
  sm: LAYOUT_DIMENSIONS.NOTE_SECTION_MAX_WIDTH + 24 * 2, // 848px
  md: LAYOUT_DIMENSIONS.NOTE_SECTION_MAX_WIDTH + 32 * 2, // 864px
} as const;

/**
 * Extra left padding when chat is visible for absolutely positioned action elements
 */
export const SECTION_GUTTER_PADDING = 78;

/**
 * Z-index values for layered components
 */
export const LAYOUT_Z_INDEX = {
  /** Z-index for approval banner (should be above content) */
  APPROVAL_BANNER: 1200,

  /** Z-index for top bar (should be above sections) */
  TOP_BAR: 1100,
} as const;

/**
 * Animation timing and easing for layout transitions
 */
export const LAYOUT_ANIMATIONS = {
  /** Duration for chat sidebar slide animations in milliseconds */
  CHAT_SLIDE_DURATION: 300,

  /** CSS cubic-bezier easing function for smooth slide animations */
  CHAT_SLIDE_EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

/**
 * DOM selectors for layout containers
 */
export const LAYOUT_SELECTORS = {
  /** Main scroll container for note sections */
  NOTE_SECTIONS_CONTAINER: '#note-sections-scroll-container',
} as const;

/**
 * Mobile drawer dimensions for narrow screens
 * Used by MobileChatDrawer component
 */
export const MOBILE_DRAWER_DIMENSIONS = {
  /** Minimum reading area plus drawer chrome and a multiline composer. */
  MIN_HEIGHT_PX: 320,
  /** Minimum drawer height as percentage of viewport height */
  MIN_HEIGHT_VH: 30,

  /** Maximum drawer height as percentage of viewport height */
  MAX_HEIGHT_VH: 90,

  /** Default drawer height as percentage of viewport height */
  DEFAULT_HEIGHT_VH: 50,

  /** Height of drag handle area in pixels */
  DRAG_HANDLE_HEIGHT: 40,
} as const;

/**
 * The drawer and note reservation resolve to the same height. When the shell
 * cannot fit both panes, chat uses the available space until the user closes it;
 * the note stays mounted with its editing state intact.
 */
export const getMobileDrawerHeightCss = (heightPercent: number): string =>
  `min(max(${MOBILE_DRAWER_DIMENSIONS.MIN_HEIGHT_PX}px, ${heightPercent}dvh), var(--app-content-height, 100dvh))`;
